import {
  ApiResponse,
  ForgotPasswordData,
  LoginCredentials,
  RegisterData,
  User,
  VerifyCodeData,
} from '@/types';
import { apiService } from './api';

export const authService = {
  // Login user
  login: async (
    credentials: LoginCredentials
  ): Promise<ApiResponse<{ user: User; token: string }>> => {
    try {
      const response = await apiService.post<{ user: User; token: string }>(
        '/auth/login',
        credentials
      );

      if (response.success && response.data) {
        // Store token in localStorage
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Check if email is available for registration
  checkEmailAvailability: async (email: string): Promise<ApiResponse<{ available: boolean }>> => {
    try {
      const response = await apiService.post<{ available: boolean }>('/auth/check-email', {
        email,
      });
      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Email check failed',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Register new user
  register: async (userData: RegisterData): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await apiService.post<{ message: string }>('/auth/register', {
        firstName: userData.firstName,
        lastName: userData.lastName,
        workEmail: userData.workEmail,
        mobileNumber: userData.mobileNumber,
        countryCode: userData.countryCode,
        companyName: userData.companyName,
        companyType: userData.companyType,
        password: userData.password,
        confirmPassword: userData.confirmPassword,
      });

      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Verify email with code
  verifyCode: async (
    verifyData: VerifyCodeData
  ): Promise<ApiResponse<{ user: User; token: string }>> => {
    try {
      const response = await apiService.post<{ user: User; token: string }>(
        '/auth/verify-code',
        verifyData
      );

      if (response.success && response.data) {
        // Store token in localStorage
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Verification failed',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Resend verification code
  resendVerificationCode: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    try {
      return await apiService.post<{ message: string }>('/auth/resend-code', { email });
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to resend verification code',
      };
    }
  },

  // Logout user
  logout: async (): Promise<void> => {
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  },

  // Get current user
  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    try {
      return await apiService.get<User>('/auth/me');
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get user data',
      };
    }
  },

  // Refresh token
  refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
    try {
      const response = await apiService.post<{ token: string }>('/auth/refresh');

      if (response.success && response.data) {
        localStorage.setItem('auth_token', response.data.token);
      }

      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Token refresh failed',
      };
    }
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('auth_token');
    return !!token;
  },

  // Get stored user data
  getStoredUser: (): User | null => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<ApiResponse<User>> => {
    try {
      const response = await apiService.put<User>('/auth/profile', userData);

      if (response.success && response.data) {
        localStorage.setItem('user', JSON.stringify(response.data));
      }

      return response;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Profile update failed',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Change password
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<ApiResponse<void>> => {
    try {
      return await apiService.put<void>('/auth/change-password', {
        currentPassword,
        newPassword,
      });
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Password change failed',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Forgot password
  forgotPassword: async (
    forgotPasswordData: ForgotPasswordData
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      return await apiService.post<{ message: string }>(
        '/auth/forgot-password',
        forgotPasswordData
      );
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Password reset request failed',
      };
    }
  },

  // Verify reset password OTP
  verifyResetPasswordOTP: async (
    email: string,
    otp: string
  ): Promise<ApiResponse<{ message: string; email: string; isValid: boolean }>> => {
    try {
      return await apiService.post<{ message: string; email: string; isValid: boolean }>(
        '/auth/verify-reset-otp',
        {
          email,
          otp,
        }
      );
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'OTP verification failed',
      };
    }
  },

  // Reset password
  resetPassword: async (
    email: string,
    otp: string,
    newPassword: string
  ): Promise<ApiResponse<{ message: string }>> => {
    try {
      return await apiService.post<{ message: string }>('/auth/reset-password', {
        email,
        otp,
        newPassword,
      });
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Password reset failed',
      };
    }
  },
};
